import React, { useEffect, useState } from 'react';
import {
    Box, Card, CardActionArea, CardContent, Dialog, DialogContent, DialogTitle, Grid, Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { RoleService } from '../../services/role.service';
import { IRoleTemplate } from '../../interfaces/role.interface';
import { ROLE_FREEFORM_ENABLED } from '../../constants/featureFlags';
import { helpWhy } from '../../help/why';

interface Props {
    open: boolean;
    onClose: () => void;
    onPick: (template: IRoleTemplate | null) => void;
    /** Template keys already used by an existing role; those cards are disabled. */
    usedTemplateKeys?: string[];
}

const TemplatePickerDialog: React.FC<Props> = ({ open, onClose, onPick, usedTemplateKeys = [] }) => {
    const { selectedInstitutionId } = useAuth();
    const toast = useToast();
    const [templates, setTemplates] = useState<IRoleTemplate[]>([]);

    useEffect(() => {
        if (!open || !selectedInstitutionId) return;
        RoleService.templates(selectedInstitutionId)
            .then(setTemplates)
            .catch(err => toast.showApiError(err, 'Failed to load role templates'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedInstitutionId]);

    const suggested = templates.filter(t => !t.admin);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-help-page="roles.template.page">
            <DialogTitle>New role</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {suggested.map(template => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.key}>
                            <Card variant="outlined">
                                <CardActionArea data-help="roles.template.pick" {...helpWhy(usedTemplateKeys.includes(template.key) ? { kind: 'status', message: 'You already have this role. Edit it from the list instead.' } : null)} onClick={() => onPick(template)} disabled={usedTemplateKeys.includes(template.key)}
                                    sx={usedTemplateKeys.includes(template.key) ? { opacity: 0.55 } : undefined}>
                                    <CardContent>
                                        <Typography variant="subtitle1">{template.displayName}</Typography>
                                        <Typography variant="body2" color="text.secondary">{template.description}</Typography>
                                        {usedTemplateKeys.includes(template.key) && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                You already have this role. Edit it from the list instead.
                                            </Typography>
                                        )}
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                    {ROLE_FREEFORM_ENABLED && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Card variant="outlined">
                                <CardActionArea data-help="roles.template.scratch" onClick={() => onPick(null)}>
                                    <CardContent>
                                        <Typography variant="subtitle1">Start from scratch</Typography>
                                        <Typography variant="body2" color="text.secondary">Build a role with no preset permissions.</Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    )}
                </Grid>
                {suggested.length === 0 && !ROLE_FREEFORM_ENABLED && (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">No role templates available.</Typography>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default TemplatePickerDialog;
